import { Chart, registerables } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import {
  getDepartureTimeData,
  getCaliforniaDepartureTimeData,
  DepartureTimeRow
} from '../../services/departureTimeService';

Chart.register(...registerables);

/** ACS departure_time_bin labels as stored in the DB (30-min splits in peak hours). */
const departureOrder: string[] = [
  '12:00 a.m. to 4:59 a.m.',
  '5:00 a.m. to 5:29 a.m.',
  '5:30 a.m. to 5:59 a.m.',
  '6:00 a.m. to 6:29 a.m.',
  '6:30 a.m. to 6:59 a.m.',
  '7:00 a.m. to 7:29 a.m.',
  '7:30 a.m. to 7:59 a.m.',
  '8:00 a.m. to 8:29 a.m.',
  '8:30 a.m. to 8:59 a.m.',
  '9:00 a.m. to 9:59 a.m.',
  '10:00 a.m. to 10:59 a.m.',
  '11:00 a.m. to 11:59 a.m.',
  '12:00 p.m. to 3:59 p.m.',
  '4:00 p.m. to 11:59 p.m.'
];

const normalizeBin = (label: string | null | undefined) => String(label || '').replace(/^Total:\s*/i, '').trim();
const canonicalKey = (label: string | null | undefined) =>
  normalizeBin(label)
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/\./g, '');

/** Combine ACS half-hour bins into hourly bars (where ACS publishes two 30-min slices). */
const HOURLY_BUCKETS: { bucketKey: string; label: string; sourceLabels: string[] }[] = [
  { bucketKey: 'h_12_4', label: '12 a.m.–5 a.m.', sourceLabels: ['12:00 a.m. to 4:59 a.m.'] },
  { bucketKey: 'h_5', label: '5–6 a.m.', sourceLabels: ['5:00 a.m. to 5:29 a.m.', '5:30 a.m. to 5:59 a.m.'] },
  { bucketKey: 'h_6', label: '6–7 a.m.', sourceLabels: ['6:00 a.m. to 6:29 a.m.', '6:30 a.m. to 6:59 a.m.'] },
  { bucketKey: 'h_7', label: '7–8 a.m.', sourceLabels: ['7:00 a.m. to 7:29 a.m.', '7:30 a.m. to 7:59 a.m.'] },
  { bucketKey: 'h_8', label: '8–9 a.m.', sourceLabels: ['8:00 a.m. to 8:29 a.m.', '8:30 a.m. to 8:59 a.m.'] },
  { bucketKey: 'h_9', label: '9–10 a.m.', sourceLabels: ['9:00 a.m. to 9:59 a.m.'] },
  { bucketKey: 'h_10', label: '10–11 a.m.', sourceLabels: ['10:00 a.m. to 10:59 a.m.'] },
  { bucketKey: 'h_11', label: '11 a.m.–12 p.m.', sourceLabels: ['11:00 a.m. to 11:59 a.m.'] },
  { bucketKey: 'h_12_15', label: '12–4 p.m.', sourceLabels: ['12:00 p.m. to 3:59 p.m.'] },
  { bucketKey: 'h_16_23', label: '4 p.m.–12 a.m.', sourceLabels: ['4:00 p.m. to 11:59 p.m.'] }
];

const CANONICAL_TO_HOURLY_BUCKET = new Map<string, string>();
for (const hb of HOURLY_BUCKETS) {
  for (const lab of hb.sourceLabels) {
    CANONICAL_TO_HOURLY_BUCKET.set(canonicalKey(lab), hb.bucketKey);
  }
}

function acsLabelForCanonical(canon: string): string {
  const found = departureOrder.find((d) => canonicalKey(d) === canon);
  return found ?? canon;
}

/** Latest year + percent-of-workers by canonical bin key (0–100). */
function computePercentByBin(rows: DepartureTimeRow[]): { year: number | null; byBin: Map<string, number> } {
  if (!rows.length) return { year: null, byBin: new Map() };
  const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
  const latestYear = years[years.length - 1];
  const latestRows = rows.filter((r) => r.year === latestYear);
  const total = latestRows.reduce((sum, r) => sum + (Number(r.estimate) || 0), 0);
  const byBin = new Map<string, number>();
  latestRows.forEach((r) => {
    const bin = canonicalKey(r.departure_time_bin);
    const value =
      r.percent != null ? Number(r.percent) : total ? ((Number(r.estimate) || 0) / total) * 100 : 0;
    byBin.set(bin, (byBin.get(bin) || 0) + value);
  });
  return { year: latestYear, byBin };
}

/** Sum percent of workers into hourly buckets (two 30-min ACS rows → one hour bar). */
function aggregateToHourly(byBin: Map<string, number>): Map<string, number> {
  const hourly = new Map<string, number>();
  for (const [canon, pct] of Array.from(byBin.entries())) {
    const bucket = CANONICAL_TO_HOURLY_BUCKET.get(canon);
    if (bucket) {
      hourly.set(bucket, (hourly.get(bucket) || 0) + pct);
    } else {
      // Unrecognized wording: keep as its own key so we do not drop data.
      hourly.set(`_raw_${canon}`, (hourly.get(`_raw_${canon}`) || 0) + pct);
    }
  }
  return hourly;
}

function hourlyLabel(bucketKey: string): string {
  if (bucketKey.startsWith('_raw_')) {
    return acsLabelForCanonical(bucketKey.slice(5));
  }
  const hb = HOURLY_BUCKETS.find((b) => b.bucketKey === bucketKey);
  return hb?.label ?? bucketKey;
}

function mergeOrderedHourlyKeys(local: Map<string, number>, state: Map<string, number>): string[] {
  const keys = new Set([...Array.from(local.keys()), ...Array.from(state.keys())]);
  const ordered: string[] = [];
  for (const hb of HOURLY_BUCKETS) {
    if (keys.has(hb.bucketKey)) ordered.push(hb.bucketKey);
  }
  Array.from(keys)
    .filter((k) => !HOURLY_BUCKETS.some((hb) => hb.bucketKey === k))
    .sort()
    .forEach((k) => ordered.push(k));
  return ordered;
}

/** Highest-share **hourly** window (same buckets/labels as the chart), not raw ACS 30-min bins. */
export const pickTopDepartureWindow = (data: DepartureTimeRow[]): string | null => {
  if (!data.length) return null;
  const { byBin } = computePercentByBin(data);
  if (!byBin.size) return null;
  const hourly = aggregateToHourly(byBin);
  if (!hourly.size) return null;
  const orderedKeys = mergeOrderedHourlyKeys(hourly, new Map());
  let maxPct = -1;
  for (const k of orderedKeys) {
    maxPct = Math.max(maxPct, hourly.get(k) ?? 0);
  }
  if (maxPct <= 0) return null;
  const topKey = orderedKeys.find((k) => (hourly.get(k) ?? 0) === maxPct);
  return topKey ? hourlyLabel(topKey) : null;
};

const COLOR_LOCAL = 'rgba(37, 99, 235, 0.85)';
const COLOR_STATE = 'rgba(220, 38, 38, 0.85)';

interface DepartureGroupedBarCanvasProps {
  rowsLocal: DepartureTimeRow[];
  rowsState: DepartureTimeRow[];
  localLabel: string;
  /** When false/empty state rows, only local bars are shown. */
  showState: boolean;
}

const DepartureGroupedBarCanvas: React.FC<DepartureGroupedBarCanvasProps> = ({
  rowsLocal,
  rowsState,
  localLabel,
  showState
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (!rowsLocal.length) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return;
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const { year: yL, byBin: localRaw } = computePercentByBin(rowsLocal);
    const { year: yS, byBin: stateRaw } = computePercentByBin(rowsState);
    const localBins = aggregateToHourly(localRaw);
    const stateBins = aggregateToHourly(stateRaw);
    const hasState = showState && rowsState.length > 0 && stateBins.size > 0;

    const orderedKeys = hasState ? mergeOrderedHourlyKeys(localBins, stateBins) : mergeOrderedHourlyKeys(localBins, new Map());
    const labels = orderedKeys.map((k) => hourlyLabel(k));
    const localValues = orderedKeys.map((k) => localBins.get(k) ?? 0);
    const stateValues = orderedKeys.map((k) => stateBins.get(k) ?? 0);

    const datasets = [
      {
        label: yL != null ? `${localLabel} (${yL})` : localLabel,
        data: localValues,
        backgroundColor: COLOR_LOCAL,
        borderRadius: 4,
        maxBarThickness: 36
      }
    ];
    if (hasState) {
      datasets.push({
        label: yS != null ? `California (${yS})` : 'California',
        data: stateValues,
        backgroundColor: COLOR_STATE,
        borderRadius: 4,
        maxBarThickness: 36
      });
    }

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        plugins: {
          legend: {
            position: 'top' as const,
            labels: { usePointStyle: true, padding: 16 }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed.y;
                if (v == null || Number.isNaN(v)) return '';
                return `${ctx.dataset.label}: ${Number(v).toFixed(1)}%`;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Percent of workers' },
            stacked: false
          },
          x: {
            stacked: false,
            title: { display: true, text: 'Departure time (hourly windows)' },
            ticks: { maxRotation: 45, minRotation: 0 }
          }
        }
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [rowsLocal, rowsState, localLabel, showState]);

  if (!rowsLocal.length) return null;

  return (
    <div style={{ height: 380 }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

export interface DepartureSummary {
  localTop: string | null;
  californiaTop: string | null;
}

interface DepartureTimeChartProps {
  location: string | null;
  /** @deprecated use onDepartureSummaryChange */
  onTopWindowChange?: (windowLabel: string | null) => void;
  onDepartureSummaryChange?: (summary: DepartureSummary) => void;
}

export const DepartureTimeChart: React.FC<DepartureTimeChartProps> = ({
  location,
  onTopWindowChange,
  onDepartureSummaryChange
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowsLocal, setRowsLocal] = useState<DepartureTimeRow[]>([]);
  const [rowsState, setRowsState] = useState<DepartureTimeRow[]>([]);
  const onTopWindowChangeRef = useRef(onTopWindowChange);
  const onDepartureSummaryChangeRef = useRef(onDepartureSummaryChange);
  onTopWindowChangeRef.current = onTopWindowChange;
  onDepartureSummaryChangeRef.current = onDepartureSummaryChange;

  const isCalifornia = (location || '').trim().toLowerCase() === 'california';
  const showStateComparison = !isCalifornia;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!location) {
        setRowsLocal([]);
        setRowsState([]);
        onTopWindowChangeRef.current?.(null);
        onDepartureSummaryChangeRef.current?.({ localTop: null, californiaTop: null });
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [local, state] = await Promise.all([
          getDepartureTimeData(location),
          showStateComparison ? getCaliforniaDepartureTimeData() : Promise.resolve([] as DepartureTimeRow[])
        ]);
        if (cancelled) return;
        setRowsLocal(local);
        setRowsState(state);
        const localTop = pickTopDepartureWindow(local);
        const californiaTop = showStateComparison ? pickTopDepartureWindow(state) : null;
        onTopWindowChangeRef.current?.(localTop);
        onDepartureSummaryChangeRef.current?.({ localTop, californiaTop });
      } catch (e) {
        if (cancelled) return;
        onTopWindowChangeRef.current?.(null);
        onDepartureSummaryChangeRef.current?.({ localTop: null, californiaTop: null });
        setError(e instanceof Error ? e.message : 'Failed to load departure time data');
        setRowsLocal([]);
        setRowsState([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [location, showStateComparison]);

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}>Loading departure time data...</div>;
  }
  if (error) {
    return <div style={{ padding: 24, color: '#dc2626' }}>{error}</div>;
  }

  if (!rowsLocal.length) {
    return <p style={{ padding: 24 }}>No departure time data for {location}.</p>;
  }

  return (
    <div>
      <h3 style={{ marginBottom: '12px' }}>Departure time to work</h3>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#475569' }}>
        {showStateComparison && rowsState.length > 0
          ? 'Hourly bars (adjacent 30-minute ACS categories combined). Grouped with California statewide where available.'
          : 'Hourly departure windows; adjacent 30-minute ACS categories are combined.'}
      </p>
      <DepartureGroupedBarCanvas
        rowsLocal={rowsLocal}
        rowsState={rowsState}
        localLabel={location || 'Selected place'}
        showState={showStateComparison}
      />
    </div>
  );
};
